import { useEffect, useState } from "react";
import { supabase } from "../../supabase/supabase";

// Call limits per phone number for V2 calls (migration 20261105120000, voxi-api-server
// call_limits/). The same two columns are on organizations and assistants:
//   max_calls_per_number_per_day  answered calls to one number per calendar day, 1 to 10
//   min_call_gap_minutes          minutes between two counted calls to one number, 0 to 1440
// Organization NULL = the platform default below; assistant NULL = no extra limit.

// Kept in step with PLATFORM_DEFAULT_ORG_LIMITS in voxi-api-server call_limits/limits.py.
export const PLATFORM_DEFAULT_PER_DAY = 3;
export const PLATFORM_DEFAULT_GAP_MINUTES = 60;
export const DEFAULT_TIMEZONE = "Asia/Kolkata";

export const PER_DAY_MIN = 1;
export const PER_DAY_MAX = 10;
export const GAP_MIN = 0;
export const GAP_MAX = 1440;

// A form field as typed: "" is no value (NULL).
function limitError(value: string, min: number, max: number, label: string): string | null {
  const text = value.trim();
  if (text === "") return null;
  if (!/^\d+$/.test(text)) return `${label} must be a whole number.`;
  const n = Number(text);
  if (n < min || n > max) return `${label} must be from ${min} to ${max}.`;
  return null;
}

export function perDayError(value: string): string | null {
  return limitError(value, PER_DAY_MIN, PER_DAY_MAX, "Calls per day");
}

export function gapError(value: string): string | null {
  return limitError(value, GAP_MIN, GAP_MAX, "Minutes between calls");
}

// "" -> null, "3" -> 3. Only call on a value with no error.
export function toColumn(value: string): number | null {
  const text = value.trim();
  return text === "" ? null : Number(text);
}

export function fromColumn(value: unknown): string {
  return typeof value === "number" ? String(value) : "";
}

// The IANA time zones this browser knows, Asia/Kolkata first.
export function timezoneOptions(saved: string): string[] {
  let zones: string[] = [];
  try {
    zones = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] })
      .supportedValuesOf?.("timeZone") ?? [];
  } catch {
    zones = [];
  }
  const list = [DEFAULT_TIMEZONE, ...zones.filter((z) => z !== DEFAULT_TIMEZONE)];
  return saved && !list.includes(saved) ? [...list, saved] : list;
}

interface OrganizationCallLimits {
  perDay: number;
  gapMinutes: number;
  loaded: boolean;
}

// The organization's limits as the check applies them (its own values, else the platform
// default), shown next to an assistant's fields as the ceiling.
export function useOrganizationCallLimits(organizationId: string | undefined): OrganizationCallLimits {
  const [state, setState] = useState<OrganizationCallLimits>({
    perDay: PLATFORM_DEFAULT_PER_DAY,
    gapMinutes: PLATFORM_DEFAULT_GAP_MINUTES,
    loaded: false,
  });

  useEffect(() => {
    setState({ perDay: PLATFORM_DEFAULT_PER_DAY, gapMinutes: PLATFORM_DEFAULT_GAP_MINUTES, loaded: false });
    if (!organizationId) return;
    let cancelled = false;
    (async () => {
      try {
        // select("*"): before the migration the columns are missing and read as the default.
        const { data, error } = await supabase
          .from("organizations")
          .select("*")
          .eq("id", organizationId)
          .maybeSingle();
        if (cancelled) return;
        if (error) throw error;
        setState({
          perDay: data?.max_calls_per_number_per_day ?? PLATFORM_DEFAULT_PER_DAY,
          gapMinutes: data?.min_call_gap_minutes ?? PLATFORM_DEFAULT_GAP_MINUTES,
          loaded: !!data,
        });
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to load the organization call limits:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  return state;
}
